import { useState } from 'react';
import { Download } from 'lucide-react';

import { notifyError } from '@/portainer/services/notifications';

import { Button, CopyButton, LoadingButton } from '@@/buttons';
import { Modal } from '@@/modals/Modal';
import { FormControl } from '@@/form-components/FormControl';
import { TextArea } from '@@/form-components/Input/Textarea';
import { DownloadButton } from '@@/buttons/DownloadButton';
import { FormError } from '@@/form-components/FormError';
import { Code } from '@@/Code';

import { useGenerateSSHKeyMutation } from './generateSSHKey.service';

export function SSHKeyGenerationModal({
  onDismiss,
}: {
  onDismiss: (cont: boolean, generatedPrivateKey?: string) => void;
}) {
  const [generatedPrivateKey, setGeneratedPrivateKey] = useState('');
  const [generatedPublicKey, setGeneratedPublicKey] = useState('');
  const generateSSHKeyMutation = useGenerateSSHKeyMutation();

  return (
    <Modal aria-label="Generate SSH key pair" size="lg">
      <Modal.Header title="Generate RSA SSH key pair" />
      <Modal.Body>
        <div className="flex w-full items-center">
          <LoadingButton
            isLoading={generateSSHKeyMutation.isLoading}
            data-cy="generateSSHModal-generateButton"
            loadingText="Generating SSH key pair..."
            size="medium"
            color="secondary"
            onClick={() => {
              generateSSHKeyMutation.mutate(undefined, {
                onSuccess: (data) => {
                  setGeneratedPrivateKey(data.private);
                  setGeneratedPublicKey(data.public);
                },
                onError: (error) => {
                  notifyError(
                    'Unable to generate SSH key pair',
                    error as Error
                  );
                },
              });
            }}
          >
            Generate SSH key pair
          </LoadingButton>
        </div>
        <div className="my-4 w-full">
          {generatedPrivateKey && (
            <FormControl label="Private key" className="[&>label]:pl-0">
              <TextArea
                value={generatedPrivateKey}
                className="min-h-[96px] resize-y"
                disabled
                data-cy="generateSSHModal-privateKeyTextArea"
              />
              <CopyDownloadActions
                fileName="private_key"
                fileContent={generatedPrivateKey}
                keyType="private"
              />
            </FormControl>
          )}
          {generatedPublicKey && (
            <div className="w-full">
              <FormControl label="Public key" className="[&>label]:pl-0">
                <>
                  <TextArea
                    value={generatedPublicKey}
                    className="min-h-[64px] resize-y"
                    disabled
                    data-cy="generateSSHModal-publicKeyTextArea"
                  />
                  <CopyDownloadActions
                    fileName="public_key.pub"
                    fileContent={generatedPublicKey}
                    keyType="public"
                  />
                </>
              </FormControl>
              <FormControl
                label="Add to authorized_keys"
                className="[&>label]:pl-0"
              >
                <Code showCopyButton>
                  {authorizedKeysCommand(generatedPublicKey)}
                </Code>
              </FormControl>
              <FormError className="!items-start [&>svg]:mt-0.5">
                Please save your public and private keys now. In addition, run
                the command above as the SSH user on each machine Portainer
                connects to.
              </FormError>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex w-full justify-between gap-2">
          <Button
            color="default"
            data-cy="generateSSHModal-cancelButton"
            size="medium"
            onClick={() => onDismiss(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            data-cy="generateSSHModal-continueButton"
            size="medium"
            disabled={!generatedPrivateKey}
            onClick={() => onDismiss(true, generatedPrivateKey)}
          >
            Continue
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

function authorizedKeysCommand(publicKey: string) {
  return `mkdir -p ~/.ssh && echo '${publicKey.trim()}' >> ~/.ssh/authorized_keys`;
}

interface CopyDownloadActionsProps {
  fileName: string;
  fileContent: string;
  keyType: 'public' | 'private';
}

function CopyDownloadActions({
  fileName,
  fileContent,
  keyType,
}: CopyDownloadActionsProps) {
  return (
    <div className="mt-2.5 flex w-full gap-2">
      <DownloadButton
        fileContent={fileContent}
        fileName={fileName}
        color="default"
        size="small"
        options={{ type: 'text/text;charset=utf-8' }}
        icon={Download}
        data-cy={`generateSSHModal-download${keyType}KeyButton`}
      >
        Download {keyType} key
      </DownloadButton>
      <CopyButton
        copyText={fileContent}
        color="default"
        data-cy={`generateSSHModal-copy${keyType}KeyButton`}
      >
        Copy {keyType} key value
      </CopyButton>
    </div>
  );
}
